import { useEffect, useState } from "react";
import type { CharterView } from "./realtime.ts";

/**
 * What the avatar was told at startup, shown verbatim.
 *
 * This is the text that was actually sent as `session.instructions`, not a
 * description of it, so what you read here is what the model read. The
 * acknowledgement below it is the model's own reply to a system check, which
 * turns "it has the briefing" from an assumption into an observation.
 */
export type Resource = {
  kind: string;
  /** `provisioning` can last minutes, so it carries a step to show. */
  status: "pending" | "provisioning" | "ready" | "failed";
  url?: string;
  error?: string;
  metadata?: { step?: string; railwayProjectId?: string; startedAt?: string };
};

/** How long the build has been going, so progress is legible as progress. */
function Elapsed({ since }: { since?: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);
  if (!since) return null;
  const secs = Math.max(0, Math.floor((Date.now() - new Date(since).getTime()) / 1000));
  const shown = secs < 90 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
  return <> · {shown}</>;
}

/**
 * What has been provisioned for this avatar, and what is still being built.
 *
 * Rendered whether or not a session has started. Building an org its own
 * workspace takes minutes and begins the moment the avatar is hired, so tying
 * this to the charter meant the user saw nothing at all during the only part
 * of the process where they cannot tell success from failure.
 */
function Resources({ resources }: { resources: Resource[] }) {
  if (resources.length === 0) return null;
  return (
    <>
      {resources.some((r) => r.status === "pending" || r.status === "provisioning") && (
        // Said plainly, because the avatar will answer perfectly well while
        // this is running and then fail at the first thing it is asked to do.
        <p className="notice building-notice">
          Setting up this avatar&rsquo;s workspace. It can talk, but its project tools
          will not work until this finishes &mdash; usually a few minutes.
        </p>
      )}

      {resources.length > 0 && (
        <div className="resources">
          {resources.map((r) => (
            <div key={r.kind} className={`resource-row ${r.status}`}>
              <span className="res-kind">{r.kind.replace(/_/g, " ")}</span>
              {r.status === "ready" && r.url ? (
                <a href={r.url} target="_blank" rel="noreferrer">
                  open ↗
                </a>
              ) : r.status === "pending" || r.status === "provisioning" ? (
                // Building an org its own workspace takes minutes. A bare
                // "provisioning…" for all of it is indistinguishable from a
                // hang, and the avatar cannot do its job until it finishes.
                <span className="muted building">
                  {r.metadata?.step ?? "queued"}
                  <Elapsed since={r.metadata?.startedAt} />
                </span>
              ) : (
                <span className="res-error" title={r.error}>
                  failed — {r.error?.slice(0, 70)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

    </>
  );
}

export function Briefing({
  charter,
  ack,
  connected,
  resources,
}: {
  charter: CharterView | null;
  ack: string | null | undefined;
  connected: boolean;
  resources: Resource[];
}) {
  const [open, setOpen] = useState<string | null>("TOOLS");

  if (!charter) {
    return (
      <section className="panel">
        <h2>Briefing</h2>
        {/* Provisioning starts at hire, long before any session. */}
        <Resources resources={resources} />
        <p className="muted">
          {connected ? "Waiting for the session…" : "Start a conversation to see what the avatar is told."}
        </p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Briefing — sent at startup</h2>

      <div className={`ack ${ack === null ? "bad" : ack ? "good" : "pending"}`}>
        {ack === undefined && <>⏳ waiting for the avatar to confirm it received this…</>}
        {ack === null && <>⚠ the avatar did not confirm its briefing. It may not have applied.</>}
        {ack && (
          <>
            <strong>Acknowledged</strong> — the avatar reports: <code>{ack}</code>
          </>
        )}
      </div>

      <Resources resources={resources} />

      <div className="tools">
        {charter.tools.length === 0 ? (
          <p className="muted">This avatar has no tools.</p>
        ) : (
          charter.tools.map((t) => (
            <div key={t.id} className="tool">
              <code>{t.id}</code>
              <span className="tool-summary">{t.summary}</span>
              {t.docsUrl && (
                <a href={t.docsUrl} target="_blank" rel="noreferrer" className="tool-doc">
                  docs ↗
                </a>
              )}
            </div>
          ))
        )}
      </div>

      <div className="charter">
        {charter.sections.map((s) => (
          <div key={s.id} className="charter-section">
            <button className="charter-head" onClick={() => setOpen(open === s.id ? null : s.id)}>
              <span className="chev">{open === s.id ? "▾" : "▸"}</span>
              {s.title}
            </button>
            {open === s.id && <pre className="charter-body">{s.body}</pre>}
          </div>
        ))}
      </div>
    </section>
  );
}
